import { upload } from '@vercel/blob/client';

const CATALOG_ENDPOINT = '/api/watchvim-json?type=catalog';

const advertisingCheckoutKeys = new Map();
const advertisingOrderKeys = new Map();

function clientUuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return [...bytes].map((b, i) => (i === 4 || i === 6 || i === 8 || i === 10 ? '-' : '') + b.toString(16).padStart(2, '0')).join('');
}

function advertisingQuoteKey(payload = {}) {
  return JSON.stringify([
    String(payload.campaignFormat || 'video').toLowerCase(),
    String(payload.campaignType || '15'),
    String(payload.billingMode || 'flat').toLowerCase(),
    String(payload.packageKey || 'starter').toLowerCase(),
    String(payload.dailyBudget ?? ''),
    String(payload.flightDays ?? ''),
  ]);
}

function first(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function slugify(value = '') {
  return String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

async function jsonResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data?.error || data?.message || `Request failed with ${response.status}`);
  return data;
}

let publicCatalogPromise = null;
export async function fetchBusinessCatalog({ force = false } = {}) {
  if (!force && publicCatalogPromise) return publicCatalogPromise;
  publicCatalogPromise = fetch(CATALOG_ENDPOINT, {
    headers: { accept: 'application/json' },
    cache: 'no-store',
  }).then(jsonResponse).catch((error) => {
    publicCatalogPromise = null;
    throw error;
  });
  return publicCatalogPromise;
}

function publishedJob(raw, index) {
  const title = String(first(raw?.title, raw?.jobTitle, raw?.name, `Opportunity ${index + 1}`));
  const status = String(first(raw?.status, raw?.state, raw?.isPublished === false ? 'draft' : 'published')).toLowerCase();
  const published = raw?.isPublished !== false && raw?.published !== false && !['draft', 'hidden', 'closed', 'archived'].includes(status);
  return {
    id: String(first(raw?.id, raw?.jobId, raw?.slug, slugify(title))),
    slug: String(first(raw?.slug, slugify(title))),
    title,
    type: String(first(raw?.type, raw?.jobType, raw?.category, raw?.employmentType, 'job')),
    location: String(first(raw?.location, raw?.city, raw?.workLocation, '')),
    summary: String(first(raw?.summary, raw?.excerpt, raw?.description, '')),
    description: String(first(raw?.description, raw?.details, raw?.summary, '')),
    responsibilities: first(raw?.responsibilities, raw?.duties, ''),
    requirements: first(raw?.requirements, raw?.qualifications, ''),
    compensation: String(first(raw?.compensation, raw?.pay, raw?.salary, '')),
    applyEmail: String(first(raw?.applyEmail, raw?.apply_email, raw?.email, '')),
    publishedAt: String(first(raw?.publishedAt, raw?.published_at, raw?.createdAt, raw?.created_at, '')),
    published,
    raw,
  };
}

export async function fetchCareers() {
  const payload = await fetchBusinessCatalog();
  const careers = payload?.careers && typeof payload.careers === 'object' ? payload.careers : {};
  const rawJobs = [payload?.jobs, careers?.jobs, careers?.listings, careers?.opportunities].find(Array.isArray) || [];
  const jobs = rawJobs.map(publishedJob).filter((job) => job.published);
  return {
    title: String(first(careers.pageTitle, careers.title, 'Careers at VIM Media')),
    subtitle: String(first(careers.pageSubtitle, careers.subtitle, 'Build the next chapter of independent streaming with WatchVIM.')),
    jobs,
  };
}

export async function fetchCastingNotices() {
  const response = await fetch('/api/casting/notices', { headers: { accept: 'application/json' }, cache: 'no-store' });
  const data = await jsonResponse(response);
  return asArray(data?.notices).map((notice) => ({
    ...notice,
    id: String(notice?.id || ''),
    slug: String(notice?.slug || notice?.id || ''),
    title: String(notice?.project_title || notice?.title || 'Casting Notice'),
  }));
}

export async function submitContentSubmission(payload) {
  return jsonResponse(await fetch('/api/submissions/start', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(payload),
  }));
}

export async function submitCareerApplication(payload) {
  return jsonResponse(await fetch('/api/careers/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(payload),
  }));
}

export async function submitCastingApplication(payload) {
  return jsonResponse(await fetch('/api/casting/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(payload),
  }));
}

export async function createAdvertisingOrder(options) {
  const payload = typeof options === 'string' ? { campaignType: options } : { ...(options || {}) };
  const quoteKey = advertisingQuoteKey(payload);
  const checkoutKey = String(payload.checkoutKey || advertisingCheckoutKeys.get(quoteKey) || clientUuid());
  advertisingCheckoutKeys.set(quoteKey, checkoutKey);
  payload.checkoutKey = checkoutKey;

  const result = await jsonResponse(await fetch('/api/paypal/ad-campaign-order/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(payload),
  }));
  if (result?.id) advertisingOrderKeys.set(String(result.id), quoteKey);
  return result;
}

export async function captureAdvertisingOrder(orderID) {
  return jsonResponse(await fetch('/api/paypal/ad-campaign-order/capture', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ orderID }),
  }));
}

function safeUploadName(name, fallback) {
  const cleaned = String(name || fallback || 'creative').replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || fallback || 'creative';
}

async function uploadAdvertisingCreative(orderID, kind, file) {
  if (!(file instanceof File) || !file.name) throw new Error(kind === 'video' ? 'Video creative is required.' : 'Company logo is required.');
  const isVideo = kind === 'video';
  const type = String(file.type || '').toLowerCase();
  if (isVideo) {
    if (type !== 'video/mp4' || !/\.mp4$/i.test(file.name)) throw new Error('Video creative must be an MP4 file.');
    if (file.size > 200 * 1024 * 1024) throw new Error('Video creative exceeds the 200 MB upload limit.');
  } else {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(type)) throw new Error('Company logo must be PNG, JPEG, or WEBP.');
    if (file.size > 10 * 1024 * 1024) throw new Error('Company logo exceeds the 10 MB upload limit.');
  }

  const folder = isVideo ? 'media' : 'logos';
  const pathname = `watchvim/ad-campaigns/${folder}/${orderID}/${safeUploadName(file.name, isVideo ? 'creative.mp4' : 'logo')}`;
  return upload(pathname, file, {
    access: 'public',
    handleUploadUrl: '/api/ad-campaigns/upload-token',
    clientPayload: JSON.stringify({ orderId: orderID, kind }),
    contentType: type,
    multipart: true,
  });
}

export async function submitAdvertisingCampaign(formData) {
  const orderID = String(formData?.get?.('orderId') || '').trim();
  const campaignFormat = String(formData?.get?.('campaignFormat') || 'video').toLowerCase();

  if (campaignFormat === 'video') {
    const media = formData.get('media');
    const logo = formData.get('logo');
    const mediaBlob = await uploadAdvertisingCreative(orderID, 'video', media);
    const logoBlob = await uploadAdvertisingCreative(orderID, 'logo', logo);
    formData.delete('media');
    formData.delete('logo');
    formData.set('mediaBlobUrl', mediaBlob.url);
    formData.set('mediaBlobPathname', mediaBlob.pathname || '');
    formData.set('logoBlobUrl', logoBlob.url);
    formData.set('logoBlobPathname', logoBlob.pathname || '');
  }

  const result = await jsonResponse(await fetch('/api/ad-campaigns/create', {
    method: 'POST',
    body: formData,
    headers: { accept: 'application/json' },
  }));
  const quoteKey = advertisingOrderKeys.get(orderID);
  if (quoteKey) advertisingCheckoutKeys.delete(quoteKey);
  if (orderID) advertisingOrderKeys.delete(orderID);
  return result;
}

export async function fetchAdvertiserReport(token) {
  const cleanToken = String(token || '').trim();
  if (!cleanToken) throw new Error('Campaign reporting token is required.');
  return jsonResponse(await fetch(`/api/ad-campaigns/report?token=${encodeURIComponent(cleanToken)}`, {
    headers: { accept: 'application/json' },
    cache: 'no-store',
  }));
}

export async function filmmakerRequest(path, { session, method = 'GET', body } = {}) {
  if (!session?.access_token) throw new Error('Log in to access the filmmaker portal.');
  const headers = { Authorization: `Bearer ${session.access_token}`, accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await fetch(`/api/filmmaker-portal/${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: method === 'GET' ? 'no-store' : undefined,
  });
  return jsonResponse(response);
}

export async function filmmakerUpload({ session, titleId, kind, file }) {
  if (!session?.access_token) throw new Error('Log in before uploading filmmaker materials.');
  if (!titleId || !kind || !(file instanceof File)) throw new Error('Title, upload kind, and file are required.');
  const formData = new FormData();
  formData.set('file', file);
  const params = new URLSearchParams({ titleId: String(titleId), kind: String(kind) });
  return jsonResponse(await fetch(`/api/filmmaker-portal/upload?${params.toString()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}`, accept: 'application/json' },
    body: formData,
  }));
}
